/**
 * useTransactionsFilters — iter-213.5 / iter-213.6
 *
 * Single source of truth for the filter state shared by all 3 transaction
 * list pages (`/history`, `/receivables`, `/pagos`). They each instantiate
 * the shared <GroupedListView> component and expose a near-identical set
 * of filter controls (search, date range, tag, cash account, groupBy).
 *
 * Before this hook, each page declared 5–7 `useState` calls plus its own
 * `hasActive` and `clear()` implementations. The duplication made it
 * tricky to add a new filter consistently and easy for the three pages
 * to drift apart over time (e.g. inconsistent clear semantics).
 *
 * Page-specific filters (History's amount + type, Receivables/Payments'
 * quickFilter) stay as page-level `useState` next to this hook — they
 * are not common enough to belong here.
 *
 * Server-side pages (`/receivables`, `/pagos`) consume `queryParams`
 * directly to feed React Query keys. Client-side pages (`/history`)
 * read the raw fields and apply their own `Array.filter` over the
 * loaded movements list.
 *
 * iter-213.6 — Optional `persistKey` enables localStorage persistence
 * scoped to the given page (e.g. `"history.filters"`,
 * `"receivables.filters"`, `"pagos.filters"`). Closing the tab and
 * coming back to the same page restores search/dateRange/tag/account/
 * groupBy. The dates restore *as-is*: if the user pinned "last week"
 * via the dropdown, those exact strings come back even if "last week"
 * now means something different. That's the simpler, more predictable
 * behaviour for a date-range filter — and avoids surprising the user
 * by silently re-shifting their window.
 *
 * @param {Object} options
 * @param {Object} [options.initial={}] — initial values for any of the
 *   common fields. Useful for deep-linking (e.g. `?account=...`).
 * @param {string} [options.persistKey] — when provided, the filter
 *   state is mirrored into `localStorage[persistKey]` and rehydrated
 *   on mount. Pass `null`/`undefined` to opt-out of persistence.
 *
 * @returns {{
 *   search: string,
 *   setSearch: (v: string) => void,
 *   dateFrom: string,
 *   setDateFrom: (v: string) => void,
 *   dateTo: string,
 *   setDateTo: (v: string) => void,
 *   tag: string,
 *   setTag: (v: string) => void,
 *   account: string,
 *   setAccount: (v: string) => void,
 *   groupBy: "day" | "week" | "month",
 *   setGroupBy: (v: string) => void,
 *   hasActive: boolean,
 *   clear: () => void,
 *   queryParams: Record<string, string | undefined>,
 * }}
 */
import { useCallback, useEffect, useMemo, useState } from "react";

const LS_NAMESPACE = "lumfi:filters:";

function readPersisted(persistKey, initial) {
  if (!persistKey || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LS_NAMESPACE + persistKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Defensive: localStorage may hold an old shape from a previous
    // release. We only return fields we recognize; everything else
    // (initial defaults, deep-link overrides) wins.
    if (parsed && typeof parsed === "object") {
      return {
        search: typeof parsed.search === "string" ? parsed.search : (initial.search || ""),
        dateFrom: typeof parsed.dateFrom === "string" ? parsed.dateFrom : (initial.dateFrom || ""),
        dateTo: typeof parsed.dateTo === "string" ? parsed.dateTo : (initial.dateTo || ""),
        tag: typeof parsed.tag === "string" ? parsed.tag : (initial.tag || ""),
        account: typeof parsed.account === "string" ? parsed.account : (initial.account || ""),
        groupBy: ["day", "week", "month", "tag"].includes(parsed.groupBy)
          ? parsed.groupBy
          : (initial.groupBy || "day"),
      };
    }
  } catch {
    // Corrupted JSON / disabled storage — fall through to defaults.
  }
  return null;
}

export function useTransactionsFilters({ initial = {}, persistKey } = {}) {
  // iter-213.6 — lazy initializer reads localStorage exactly once on
  // mount. Subsequent mounts of OTHER `useTransactionsFilters()` instances
  // (e.g. the same page re-opened in a new tab) get their own snapshot.
  const persisted = useMemo(
    () => readPersisted(persistKey, initial),
    // We want to read exactly once at mount — `initial` may be a fresh
    // object literal on every render, so we don't include it. `persistKey`
    // is a static string per call-site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [persistKey],
  );
  const seed = persisted || initial;
  // iter-213.9 — Track whether the SEED state came from localStorage
  // AND included at least one non-empty filter, so pages can render a
  // "Filtros restaurados · Limpiar" affordance (Notion/Linear style).
  // We compute this from the seed snapshot at mount; once the user
  // clicks Clear or changes any filter we flip it back to false so
  // the banner disappears cleanly.
  const initiallyRestored = !!(persisted && (
    persisted.search || persisted.dateFrom || persisted.dateTo ||
    persisted.tag || persisted.account
  ));
  const [restored, setRestored] = useState(initiallyRestored);

  const [search, setSearchState] = useState(seed.search || "");
  const [dateFrom, setDateFromState] = useState(seed.dateFrom || "");
  const [dateTo, setDateToState] = useState(seed.dateTo || "");
  const [tag, setTagState] = useState(seed.tag || "");
  const [account, setAccountState] = useState(seed.account || "");
  const [groupBy, setGroupBy] = useState(seed.groupBy || "day");

  // Wrap setters so any explicit user-driven change clears the
  // "restored from storage" banner (the value is now user-owned).
  const setSearch = useCallback((v) => { setSearchState(v); setRestored(false); }, []);
  const setDateFrom = useCallback((v) => { setDateFromState(v); setRestored(false); }, []);
  const setDateTo = useCallback((v) => { setDateToState(v); setRestored(false); }, []);
  const setTag = useCallback((v) => { setTagState(v); setRestored(false); }, []);
  const setAccount = useCallback((v) => { setAccountState(v); setRestored(false); }, []);

  const hasActive = !!(search || dateFrom || dateTo || tag || account);

  const clear = useCallback(() => {
    setSearchState("");
    setDateFromState("");
    setDateToState("");
    setTagState("");
    setAccountState("");
    setRestored(false);
  }, []);

  // Persist on any change. We debounce-by-batching (React batches state
  // updates so this effect fires once per render even when 5 setters
  // are called together by `clear()`).
  useEffect(() => {
    if (!persistKey || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        LS_NAMESPACE + persistKey,
        JSON.stringify({ search, dateFrom, dateTo, tag, account, groupBy }),
      );
    } catch {
      // Quota exceeded / disabled storage — silent no-op.
    }
  }, [persistKey, search, dateFrom, dateTo, tag, account, groupBy]);

  // Memoized object that pages can spread into React Query keys. The
  // `undefined` values get serialized out by axios so the URL stays
  // clean (`?search=foo` instead of `?search=foo&date_from=`).
  const queryParams = useMemo(
    () => ({
      search: search || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      tag: tag || undefined,
    }),
    [search, dateFrom, dateTo, tag],
  );

  return {
    search, setSearch,
    dateFrom, setDateFrom,
    dateTo, setDateTo,
    tag, setTag,
    account, setAccount,
    groupBy, setGroupBy,
    hasActive,
    clear,
    queryParams,
    // iter-213.9 — true on first render IFF the initial filter state
    // was rehydrated from localStorage AND included a non-empty value.
    // Resets to false on any user change or clear.
    restored,
  };
}
